"use client"

import { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Badge } from "../../../components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Search, Calendar } from "lucide-react"
import { pdf, Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Styles
const styles = StyleSheet.create({
  page: { padding: 30 },
  section: { marginBottom: 10 },
  row: { flexDirection: 'row', marginBottom: 5 },
  cell: { flex: 1, fontSize: 10 },
});

// The actual document
const PDFDocument = ({ data, selectedFields }: { data: any[]; selectedFields: string[] }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {data.map((row, rowIndex) => (
        <View key={rowIndex} style={styles.row}>
          {selectedFields.map((field, i) => (
            <Text key={i} style={styles.cell}>
              {row[field] || ''}
            </Text>
          ))}
        </View>
      ))}
    </Page>
  </Document>
);

const AVAILABLE_FIELDS = [
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "form", label: "Form" },
  { key: "status", label: "Status" },
  { key: "volunteer", label: "Volunteer" },
  { key: "submissionDate", label: "Submitted Date" },
  { key: "phone", label: "Phone" },
];

export function PortalRegistrantsSection({ portals }: any) {
  const [searchTerm, setSearchTerm] = useState("")
  const [formFilter, setFormFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [portalFilter, setPortalFilter] = useState("all")
  const REGISTRANTS_PER_PAGE = 60
  const [currentPage, setCurrentPage] = useState(1)

  // Export States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<"csv" | "pdf" | "txt" | null>(null);
  const [txtStyle, setTxtStyle] = useState<"comma" | "table">("comma"); // Added style for TXT
  const [selectedFields, setSelectedFields] = useState<string[]>([
    "name",
    "email",
    "form",
    "status",
    "volunteer",
    "submissionDate",
    "phone"
  ]);
  const [includeCrossReference, setIncludeCrossReference] = useState(false);

  const unselectedFields = AVAILABLE_FIELDS.filter(
    (field) => !selectedFields.includes(field.key)
  );

  const registrants = portals.flatMap((portal: any) =>
    portal.data.map((item: any) => {
      const name =
        item.first_name || item.surname
          ? `${item.first_name || ""} ${item.surname || ""}`.trim()
          : item.full_name || item.title?.rendered || "Unnamed"

      return {
        id: item.id,
        name,
        email: item.email_address,
        phone: item.phone_number,
        form: portal.portal,
        status: item.volunteer?.trim()
          ? "Volunteer"
          : item.vip?.toLowerCase().trim() === "true"
            ? "VIP"
            : "confirmed",
        volunteer: item.volunteer?.trim() ? item.volunteer?.trim() : "No Post",
        submissionDate: new Date(item.date).toLocaleDateString(),
      }
    })
  )

  const filteredRegistrants = registrants.filter((registrant: any) => {
    const matchesSearch =
      registrant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registrant.email.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesForm = formFilter === "all" || registrant.form === formFilter
    const matchesStatus = statusFilter === "all" || registrant.status === statusFilter
    const matchesPortal = portalFilter === "all" || registrant.form === portalFilter

    return matchesSearch && matchesForm && matchesStatus && matchesPortal
  })

  const totalPages = Math.ceil(filteredRegistrants.length / REGISTRANTS_PER_PAGE)

  const paginatedRegistrants = filteredRegistrants.slice(
    (currentPage - 1) * REGISTRANTS_PER_PAGE,
    currentPage * REGISTRANTS_PER_PAGE
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, formFilter, statusFilter, portalFilter])

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case "volunteer":
        return "default"
      case "vip":
        return "destructive"
      case "confirmed":
        return "outline"
      default:
        return "outline"
    }
  }

  const exportToCSV = (data: any[]) => {
    const csvRows = [
      selectedFields.join(","),
      ...data.map(row => selectedFields.map(f => JSON.stringify(row[f] || "")).join(","))
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "registrants.csv";
    a.click();
  };

  const exportToPDF = async (data: any[]) => {
    const blob = await pdf(<PDFDocument data={data} selectedFields={selectedFields} />).toBlob();

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'registrants.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- NEW: Export to TXT ---
  const exportToTXT = (data: any[]) => {
    let content = "";

    if (txtStyle === "comma") {
      // Formats items into comma separated format e.g., "val1, val2, val3" or single field lists like "email1, email2"
      content = data
        .map((row) =>
          selectedFields
            .map((field) => row[field] || "")
            .filter(Boolean)
            .join(", ")
        )
        .filter(Boolean)
        .join(", ");
    } else {
      // Table style formatting with aligned padded columns
      const colWidths: { [key: string]: number } = {};

      selectedFields.forEach((field) => {
        const maxLength = Math.max(
          field.length,
          ...data.map((row) => String(row[field] || "").length)
        );
        colWidths[field] = maxLength + 3; // 3 padding spaces
      });

      const header = selectedFields
        .map((f) => f.toUpperCase().padEnd(colWidths[f]))
        .join("");
      const separator = "-".repeat(header.length);
      const rows = data.map((row) =>
        selectedFields
          .map((f) => String(row[f] || "").padEnd(colWidths[f]))
          .join("")
      );

      content = [header, separator, ...rows].join("\n");
    }

    const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registrants.txt`;
    a.click();
  };

  const handleExport = () => {
    const mergedData = filteredRegistrants.map((registrant: any) => {
      const finalEntry: any = {};

      selectedFields.forEach(field => {
        const value = registrant[field];

        if (value || !includeCrossReference) {
          finalEntry[field] = value || "";
        } else {
          const match = registrants.find(
            (r: any) => r.email === registrant.email && r[field]
          );
          finalEntry[field] = match ? match[field] : "";
        }
      });

      return finalEntry;
    });

    if (exportFormat === "csv") {
      exportToCSV(mergedData);
    } else if (exportFormat === "pdf") {
      exportToPDF(mergedData);
    } else if (exportFormat === "txt") {
      exportToTXT(mergedData);
    }

    setExportDialogOpen(false);
  };

  const formOptions = Array.from(new Set(registrants.map((r: any) => r.form)))
  const statusOptions = Array.from(new Set(registrants.map((r: any) => r.status)))

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search registrants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={formFilter} onValueChange={setFormFilter}>
          <SelectTrigger className="w-full lg:w-[200px]">
            <SelectValue placeholder="Filter by form" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Forms</SelectItem>
            {formOptions.map((form: any) => (
              <SelectItem key={form} value={form}>
                {form}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full lg:w-[150px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {statusOptions.map((status: any) => (
              <SelectItem key={status} value={status}>
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={portalFilter} onValueChange={setPortalFilter}>
          <SelectTrigger className="w-full lg:w-[150px]">
            <SelectValue placeholder="Filter by portal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Portals</SelectItem>
            {portals.map((p: any) => (
              <SelectItem key={p.portal} value={p.portal}>
                {p.portal}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Added TXT export option button */}
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setExportFormat("csv"); setExportDialogOpen(true); }}>CSV</Button>
          <Button variant="outline" onClick={() => { setExportFormat("pdf"); setExportDialogOpen(true); }}>PDF</Button>
          <Button variant="outline" onClick={() => { setExportFormat("txt"); setExportDialogOpen(true); }}>TXT</Button>
        </div>
      </div>

      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Customize {exportFormat?.toUpperCase()} Export</DialogTitle>
            <DialogDescription>Select the fields to include and finalize export.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Options specific to TXT output style */}
            {exportFormat === "txt" && (
              <div className="space-y-2 border-b pb-3">
                <h4 className="font-medium text-sm">TXT Format Style</h4>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="txtStyle"
                      value="comma"
                      checked={txtStyle === "comma"}
                      onChange={() => setTxtStyle("comma")}
                    />
                    Comma Separated (e.g. email1, email2)
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="txtStyle"
                      value="table"
                      checked={txtStyle === "table"}
                      onChange={() => setTxtStyle("table")}
                    />
                    Table Format
                  </label>
                </div>
              </div>
            )}

            {/* Selected Fields */}
            <div>
              <h4 className="font-medium text-sm mb-2">Selected Fields</h4>
              <div className="flex flex-wrap gap-2">
                {selectedFields.map((fieldKey) => (
                  <Badge key={fieldKey} variant="secondary">
                    {fieldKey}
                    <button
                      onClick={() => setSelectedFields(selectedFields.filter((f) => f !== fieldKey))}
                      className="ml-1 text-red-500"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Add More Fields */}
            {unselectedFields.length > 0 && (
              <div>
                <h4 className="font-medium text-sm mb-2">Add More Fields</h4>
                <div className="flex flex-wrap gap-2">
                  {unselectedFields.map((field) => (
                    <Button
                      key={field.key}
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFields([...selectedFields, field.key])}
                    >
                      + {field.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Cross-reference toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="crossRef"
                checked={includeCrossReference}
                onChange={(e) => setIncludeCrossReference(e.target.checked)}
              />
              <label htmlFor="crossRef" className="text-sm">
                Fill missing values from other portals
              </label>
            </div>
          </div>

          {/* Export Button */}
          <div className="pt-4 flex justify-end">
            <Button onClick={() => handleExport()}>Export as {exportFormat?.toUpperCase()}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Form</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>volunteer</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Phone</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedRegistrants.map((r: any) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>{r.email}</TableCell>
                <TableCell>{r.form}</TableCell>
                <TableCell>
                  <Badge variant={getStatusBadgeVariant(r.status)}>{r.status}</Badge>
                </TableCell>
                <TableCell>{r.volunteer}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    {r.submissionDate}
                  </div>
                </TableCell>
                <TableCell>{r.phone}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <p className="text-sm text-muted-foreground">
          Showing {(currentPage - 1) * REGISTRANTS_PER_PAGE + 1}–
          {Math.min(currentPage * REGISTRANTS_PER_PAGE, filteredRegistrants.length)} of {filteredRegistrants.length} registrants
        </p>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
            First
          </Button>
          <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
            Prev
          </Button>

          {[...Array(totalPages)].map((_, idx) => {
            const page = idx + 1
            return (
              <Button
                key={page}
                variant={currentPage === page ? "default" : "outline"}
                size="sm"
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Button>
            )
          }).slice(
            Math.max(0, currentPage - 3),
            Math.min(currentPage + 2, totalPages)
          )}

          <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>
            Next
          </Button>
          <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
            Last
          </Button>
        </div>
      </div>
    </div>
  )
}